import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { APP_VERSION } from "../lib/timezone";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  MessageSquarePlus, ShieldAlert, Copy, CheckCircle2, ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Suggestion" },
  { value: "confusing", label: "Confusing / hard to use" },
];
const IMPORTANCE = [
  { value: "none", label: "Not set" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

function detectDevice() {
  const w = window.innerWidth;
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone/i.test(ua) || w < 640) return "Mobile";
  if (/Tablet|iPad/i.test(ua) || w < 1024) return "Tablet";
  return "Desktop";
}
function detectBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome/") && !ua.includes("Chromium")) return "Chrome";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Safari/") && !ua.includes("Chrome")) return "Safari";
  return "Other";
}

const emptyForm = {
  feedback_type: "bug", description: "", severity: "none", confirm_no_sensitive: false,
};

export default function FeedbackButton({ variant = "floating" }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { reference }

  if (!user || !user.pilot_feedback_enabled) return null;

  const openModal = () => {
    setForm(emptyForm);
    setResult(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Please add a short description."); return; }
    if (!form.confirm_no_sensitive) { toast.error("Please confirm you have not included sensitive information."); return; }
    setBusy(true);
    try {
      const payload = {
        feedback_type: form.feedback_type,
        description: form.description.trim(),
        severity: form.severity === "none" ? null : form.severity,
        confirm_no_sensitive: form.confirm_no_sensitive,
        // Auto-captured context (user, role, division, org, timestamp are recorded server-side)
        page_feature: location.pathname,
        route: location.pathname,
        browser: detectBrowser(),
        device: detectDevice(),
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        app_version: user.app_version || APP_VERSION,
      };
      const { data } = await api.post("/feedback", payload);
      setResult({ reference: data.reference });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyRef = () => {
    navigator.clipboard?.writeText(result.reference);
    toast.success("Reference number copied");
  };

  return (
    <>
      {/* Trigger — sidebar item (above Sign out) or persistent floating pill */}
      {variant === "sidebar" ? (
        <button
          data-testid="send-feedback-button"
          onClick={openModal}
          className="mb-2 flex w-full items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium text-slate-400 transition-colors duration-200 hover:bg-slate-800 hover:text-white"
        >
          <MessageSquarePlus className="h-4 w-4" />
          Send Feedback
        </button>
      ) : (
        <button
          data-testid="send-feedback-button"
          onClick={openModal}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg ring-1 ring-black/5 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-slate-800"
        >
          <MessageSquarePlus className="h-4 w-4" />
          <span className="hidden sm:inline">Send Feedback</span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="feedback-dialog">
          {result ? (
            <div className="py-4 text-center" data-testid="feedback-success">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
              <h3 className="mt-3 font-display text-xl font-bold text-slate-900">Thank you. Your feedback has been submitted.</h3>
              <p className="mt-2 text-sm text-slate-500">Keep this reference number for follow-up:</p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <span data-testid="feedback-reference" className="rounded-md bg-slate-100 px-4 py-2 font-mono text-lg font-bold text-slate-900">{result.reference}</span>
                <Button data-testid="copy-reference-button" variant="outline" size="icon" onClick={copyRef}><Copy className="h-4 w-4" /></Button>
              </div>
              <div className="mt-6 flex justify-center gap-2">
                <Button variant="outline" onClick={() => { setOpen(false); navigate("/my-feedback"); }} data-testid="view-my-feedback-button" className="gap-2">
                  <ListChecks className="h-4 w-4" /> View my submissions
                </Button>
                <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700 text-white">Done</Button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Send Feedback</DialogTitle>
                <DialogDescription>Quick feedback for the WatchRelay pilot. Reports go to your organization admins.</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label className={labelCls}>Category</Label>
                  <Select value={form.feedback_type} onValueChange={(v) => setForm({ ...form, feedback_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="feedback-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className={labelCls}>Description</Label>
                  <Textarea data-testid="feedback-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" placeholder="Describe your feedback" />
                </div>

                <div>
                  <Label className={labelCls}>Importance (optional)</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="feedback-importance-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {IMPORTANCE.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-start gap-2 text-xs text-amber-800">
                    <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                    Do not include CAD, NCIC, medical, personnel, criminal-history, victim, suspect, caller, patient, or other sensitive information.
                  </p>
                </div>

                <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-800">
                  <Checkbox data-testid="feedback-sensitive-confirm" checked={form.confirm_no_sensitive} onCheckedChange={(v) => setForm({ ...form, confirm_no_sensitive: !!v })} className="mt-0.5" />
                  I have not included sensitive operational information.
                </label>
              </div>

              <DialogFooter className="mt-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="feedback-submit-button" disabled={busy || !form.confirm_no_sensitive} onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {busy ? "Submitting…" : "Submit feedback"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
